import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

const Presupuestos = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const presupuestosData = [
    { id: 1, obra: 'Pozo XY-123', cliente: 'YPF S.A.', presupuesto: 5200000, ejecutado: 4100000, estado: 'En curso' },
    { id: 2, obra: 'Pozo AB-456', cliente: 'Pan American', presupuesto: 3800000, ejecutado: 1900000, estado: 'En curso' },
    { id: 3, obra: 'Workover MZ-789', cliente: 'Tecpetrol', presupuesto: 1200000, ejecutado: 1380000, estado: 'Sobrecosto' },
    { id: 4, obra: 'Perforación NW-012', cliente: 'YPF S.A.', presupuesto: 6500000, ejecutado: 1300000, estado: 'En curso' },
    { id: 5, obra: 'Mantenimiento TK-001', cliente: 'Vista Oil', presupuesto: 800000, ejecutado: 720000, estado: 'Por finalizar' },
  ];

  const getProgressColor = (ejecutado, presupuesto) => {
    const porcentaje = (ejecutado / presupuesto) * 100;
    if (porcentaje > 100) return theme.palette.error.main;
    if (porcentaje > 80) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Presupuestos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control presupuestario por obra
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nuevo Presupuesto
        </Button>
      </Box>

      {/* Resumen Visual */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {presupuestosData.slice(0, 4).map((pres) => {
          const porcentaje = Math.round((pres.ejecutado / pres.presupuesto) * 100);
          return (
            <Grid item xs={12} md={6} key={pres.id}>
              <Card
                sx={{
                  borderLeft: `4px solid ${getProgressColor(pres.ejecutado, pres.presupuesto)}`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {pres.obra}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {pres.cliente}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${porcentaje}%`}
                      icon={porcentaje > 100 ? <WarningIcon /> : <CheckIcon />}
                      color={porcentaje > 100 ? 'error' : porcentaje > 80 ? 'warning' : 'success'}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(porcentaje, 100)}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: alpha(theme.palette.grey[400], 0.2),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getProgressColor(pres.ejecutado, pres.presupuesto),
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Ejecutado: ${(pres.ejecutado / 1000000).toFixed(2)}M
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Presupuesto: ${(pres.presupuesto / 1000000).toFixed(2)}M
                    </Typography>
                  </Box>
                  {porcentaje > 100 && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      ⚠️ Requiere aprobación gerencial para continuar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Tabla Completa */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Todos los Presupuestos
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Obra</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell align="right">Presupuesto</TableCell>
                  <TableCell align="right">Ejecutado</TableCell>
                  <TableCell align="center">Avance</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {presupuestosData.map((row) => {
                  const porcentaje = Math.round((row.ejecutado / row.presupuesto) * 100);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{row.obra}</Typography>
                      </TableCell>
                      <TableCell>{row.cliente}</TableCell>
                      <TableCell align="right">${row.presupuesto.toLocaleString()}</TableCell>
                      <TableCell align="right">${row.ejecutado.toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${porcentaje}%`}
                          size="small"
                          color={porcentaje > 100 ? 'error' : porcentaje > 80 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.estado}
                          size="small"
                          variant="outlined"
                          color={row.estado === 'Sobrecosto' ? 'error' : 'primary'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined">Ver Detalle</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo Presupuesto</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nombre de Obra" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select label="Cliente">
                  <MenuItem value="1">YPF S.A.</MenuItem>
                  <MenuItem value="2">Pan American Energy</MenuItem>
                  <MenuItem value="3">Tecpetrol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Obra</InputLabel>
                <Select label="Tipo de Obra">
                  <MenuItem value="perforacion">Perforación</MenuItem>
                  <MenuItem value="workover">Workover</MenuItem>
                  <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monto Total" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha Inicio" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha Fin Estimada" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Crear Presupuesto</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Presupuestos;
